/**
 * Icon picker modal for the Action Buttons designer. Two tabs:
 *  - Icon library: a searchable grid of lucide icons from the single lazily
 *    loaded icon map (utils/lucideIconMap — one chunk, not one-per-icon).
 *    Stores the kebab-case name string that DynamicActionIcon renders.
 *  - Upload: PNG/JPEG/WebP/SVG posted to admin-upload.php (kind=action-icon).
 *    Returns the stored /uploads/ URL; rendered elsewhere as <img>.
 */

import React, { useEffect, useMemo, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Upload, Loader2, Search } from 'lucide-react';
import type { ActionButtonIcon } from '../../config/actionButtons';
import { loadLucideIcons, toKebabIconName } from '../../utils/lucideIconMap';

const API_BASE = window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';
const GRID_CAP = 150;

interface Props {
  /** Namespaces uploaded files. 0 = universal (not tied to a retailer). */
  retailerId: number;
  onSelect: (icon: ActionButtonIcon) => void;
  onClose: () => void;
}

export const IconPicker: React.FC<Props> = ({ retailerId, onSelect, onClose }) => {
  const [tab, setTab] = useState<'lucide' | 'upload'>('lucide');
  const [iconMap, setIconMap] = useState<Record<string, LucideIcon> | null>(null);
  const [query, setQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadLucideIcons()
      .then((map) => { if (!cancelled) setIconMap(map); })
      .catch(() => { if (!cancelled) setIconMap({}); });
    return () => { cancelled = true; };
  }, []);

  const names = useMemo(() => (iconMap ? Object.keys(iconMap).sort() : []), [iconMap]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? names.filter((n) => n.toLowerCase().includes(q)) : names;
    return list.slice(0, GRID_CAP);
  }, [names, query]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('kind', 'action-icon');
      fd.append('retailerId', String(retailerId));
      fd.append('file', file);
      const res = await fetch(`${API_BASE}/admin-upload.php`, { method: 'POST', credentials: 'include', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || 'Upload failed');
      onSelect({ type: 'upload', url: data.url as string });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="modal d-block fade show"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1090 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">Choose an icon</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <div className="modal-body">
            <ul className="nav nav-pills gap-1 mb-3">
              <li className="nav-item">
                <button type="button" className={`nav-link ${tab === 'lucide' ? 'active' : ''}`} onClick={() => setTab('lucide')}>Icon library</button>
              </li>
              <li className="nav-item">
                <button type="button" className={`nav-link ${tab === 'upload' ? 'active' : ''}`} onClick={() => setTab('upload')}>Upload</button>
              </li>
            </ul>

            {tab === 'lucide' ? (
              <>
                <div className="input-group input-group-sm mb-3" style={{ maxWidth: 320 }}>
                  <span className="input-group-text"><Search size={14} /></span>
                  <input className="form-control" placeholder="Search icons…" value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>
                {iconMap === null ? (
                  <div className="text-muted small d-flex align-items-center gap-2">
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading icons…
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {filtered.map((name) => {
                      const Icon = iconMap[name];
                      return (
                        <button
                          key={name}
                          type="button"
                          title={name}
                          className="btn btn-outline-secondary p-2 d-inline-flex align-items-center justify-content-center"
                          style={{ width: 44, height: 44 }}
                          onClick={() => { onSelect({ type: 'lucide', name: toKebabIconName(name) }); onClose(); }}
                        >
                          <Icon size={20} />
                        </button>
                      );
                    })}
                    {filtered.length === 0 && <span className="text-muted small">No icons match.</span>}
                    {query.trim() === '' && names.length > filtered.length && (
                      <span className="text-muted small w-100 mt-1">Showing the first {filtered.length}. Type to search all {names.length} icons.</span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div>
                <p className="text-muted small">
                  Upload a PNG or SVG (small and square works best). SVGs are sanitized server-side and
                  served as images.
                </p>
                {error && <div className="alert alert-danger py-2 px-3 small">{error}</div>}
                <label className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 mb-0">
                  {uploading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={14} />}
                  Choose file
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,.svg"
                    className="d-none"
                    disabled={uploading}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }}
                  />
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IconPicker;
